import omit from 'lodash-es/omit';
import merge from 'lodash-es/merge';
import isEqual from 'lodash-es/isEqual';
import isFunction from 'lodash-es/isFunction';
import isString from 'lodash-es/isString';
import isNumber from 'lodash-es/isNumber';
import pick from 'lodash-es/pick';

import getDisabledStyle from '../css/disabledStyle';
import updateStyle from '../css/updateStyle';
import lookAndFeel from '../lookAndFeel';
import updateStyleByRef from '../css/updateStyleByRef';
import Control from './control';
import { getDefaultControl, getDefaultControlStyle } from './combo.defaults';

export default class Combo extends Control {
  constructor(instance, path, nicePath) {
    super(instance, path, nicePath);

    // Override text API to work with combo's label instead of value
    this.addApi(this, 'text', () => {
      const val = this.value;
      if (!val) {
        return '';
      }

      // If value is object with label, return label
      if (val && typeof val === 'object' && val.label !== undefined) {
        return val.label;
      }

      // If value is string/number, find matching option and return its label
      const options = this._controlOptions.options || [];
      for (let i = 0; i < options.length; i++) {
        const opt = options[i];
        const optValue = typeof opt === 'string' ? opt : opt.value;
        if (optValue == val) {
          return typeof opt === 'string' ? opt : (opt.label || opt.value);
        }
      }

      // Fallback to value itself
      return val + '';
    });

    // Override setText to find option by label and set its value
    this.setText = function(label) {
      if (label === undefined || label === null) {
        this.value = undefined;
        this.render();
        return;
      }

      const labelStr = label + '';
      const options = this._controlOptions.options || [];

      // Find option with matching label
      for (let i = 0; i < options.length; i++) {
        const opt = options[i];
        const optLabel = typeof opt === 'string' ? opt : (opt.label || opt.value);
        const optValue = typeof opt === 'string' ? opt : opt.value;

        if (optLabel == labelStr) {
          // Set value as object with both value and label
          this.value = {
            value: optValue,
            label: optLabel
          };
          this.render();
          return;
        }
      }

      // If no matching label found, log warning
      console.warn(`Combo.setText: No option found with label "${labelStr}"`);
    };

    // Add item to options list
    this.addItem = function(choice) {
      if (!this._controlOptions.options) {
        this._controlOptions.options = [];
      }

      // Avoid duplicates - check if item already exists
      const options = this._controlOptions.options;
      const choiceValue = typeof choice === 'string' ? choice : choice.value;
      const choiceLabel = typeof choice === 'string' ? choice : (choice.label || choice.value);

      for (let i = 0; i < options.length; i++) {
        const opt = options[i];
        const optValue = typeof opt === 'string' ? opt : opt.value;
        const optLabel = typeof opt === 'string' ? opt : (opt.label || opt.value);

        if (optValue == choiceValue && optLabel == choiceLabel) {
          console.warn(`Combo.addItem: Item with value "${choiceValue}" and label "${choiceLabel}" already exists`);
          return;
        }
      }

      // Add the item
      this._controlOptions.options.push(choice);
      delete this.renderedOptions; // Force re-render of options
      this.render();
    };

    // Remove item from options list
    this.removeItem = function(choice) {
      if (!this._controlOptions.options) {
        return;
      }

      const options = this._controlOptions.options;
      const choiceValue = typeof choice === 'string' ? choice : choice.value;
      const choiceLabel = typeof choice === 'string' ? choice : (choice.label || choice.value);

      // Find and remove the item
      for (let i = options.length - 1; i >= 0; i--) {
        const opt = options[i];
        const optValue = typeof opt === 'string' ? opt : opt.value;
        const optLabel = typeof opt === 'string' ? opt : (opt.label || opt.value);

        if (optValue == choiceValue && optLabel == choiceLabel) {
          options.splice(i, 1);
          delete this.renderedOptions; // Force re-render of options

          // If removed item was selected, clear selection
          const currentValue = this.value;
          if (currentValue) {
            const currentVal = typeof currentValue === 'object' ? currentValue.value : currentValue;
            if (currentVal == choiceValue) {
              this.value = undefined;
            }
          }

          this.render();
          return;
        }
      }

      console.warn(`Combo.removeItem: Item not found`);
    };

    // Get options as a Map
    this.getOptions = function() {
      const optionsMap = new Map();
      const options = this._controlOptions.options || [];

      options.forEach(opt => {
        const value = typeof opt === 'string' ? opt : opt.value;
        const label = typeof opt === 'string' ? opt : (opt.label || opt.value);
        optionsMap.set(value, label);
      });

      return optionsMap;
    };

    // Set options from Map or array
    this.setOptions = function(options) {
      let optionsArray = [];

      if (options instanceof Map) {
        // Convert Map to array of {label, value}
        options.forEach((label, value) => {
          optionsArray.push({ label, value });
        });
      } else if (Array.isArray(options)) {
        optionsArray = options;
      } else {
        console.error('setOptions expects a Map or array', options);
        return;
      }

      this._controlOptions.options = optionsArray;
      delete this.renderedOptions; // Force re-render of options
      this.render();
    };

    // Clear selection
    this.clearSelection = function() {
      this.value = undefined;
      this.render();
    };

    // Select item by label/key, or by explicit (label, value) pair.
    //
    // Two forms:
    //   selectItem(labelOrKey)      — 1-arg: match against option.label first,
    //                                 fall back to option.value. Simple-option
    //                                 combos (string list) match on the string;
    //                                 detailed-option combos match on either
    //                                 field, so the caller can pass whichever
    //                                 side of the pair the datasource exposes.
    //   selectItem(label, value)    — 2-arg: skip matching entirely, set both
    //                                 fields directly. Use this when the
    //                                 datasource hasn't populated options yet
    //                                 or the desired value isn't in the list.
    //
    // Always calls render(true) so the visible selection updates even when the
    // control has renderControl:'once' (suspend=true after init).
    this.selectItem = function(choice, valueArg) {
      // 2-arg form: caller supplied explicit label + value, no lookup needed.
      if (arguments.length >= 2) {
        this.value = {
          label: choice,
          value: valueArg
        };
        this.render(true);
        return;
      }

      if (choice === undefined || choice === null) {
        this.value = undefined;
        this.render(true);
        return;
      }

      const choiceStr = choice + '';
      const options = this._controlOptions.options || [];

      // Prefer label match — matches simple-option strings and detailed-option
      // labels alike. Fall back to value match so callers holding a key can
      // still select on detailed-option combos.
      let matched = null;
      for (let i = 0; i < options.length; i++) {
        const opt = options[i];
        const optLabel = typeof opt === 'string' ? opt : (opt.label || opt.value);
        if (optLabel == choiceStr) {
          matched = opt;
          break;
        }
      }

      if (!matched) {
        for (let i = 0; i < options.length; i++) {
          const opt = options[i];
          const optValue = typeof opt === 'string' ? opt : opt.value;
          if (optValue == choiceStr) {
            matched = opt;
            break;
          }
        }
      }

      if (matched) {
        const optLabel = typeof matched === 'string' ? matched : (matched.label || matched.value);
        const optValue = typeof matched === 'string' ? matched : matched.value;
        this.value = {
          label: optLabel,
          value: optValue
        };
        this.render(true);
        return;
      }

      console.warn(`Combo.selectItem: No option found matching "${choiceStr}"`);
    };

    // Select item by value
    this.selectItemValue = function(value) {
      if (value === undefined || value === null) {
        this.value = undefined;
        this.render();
        return;
      }

      const valueStr = value + '';
      const options = this._controlOptions.options || [];

      // Find option with matching value
      for (let i = 0; i < options.length; i++) {
        const opt = options[i];
        const optValue = typeof opt === 'string' ? opt : opt.value;
        const optLabel = typeof opt === 'string' ? opt : (opt.label || opt.value);

        if (optValue == valueStr) {
          // Set value as object with both value and label
          this.value = {
            value: optValue,
            label: optLabel
          };
          this.render();
          return;
        }
      }

      console.warn(`Combo.selectItemValue: No option found with value "${valueStr}"`);
    };
  }

  renderElement() {
    const control = this;
    const options = this._controlOptions;
    const instance = this.instance;
    const path = this.path;

    if (!control.el) {
      delete control.renderedOptions;
      control.el = getDefaultControl(instance);
      if (!control.isLayoutMode) {
        var bodyEl = control.el.getElementsByClassName('body')[0];
        bodyEl.addEventListener('mouseover', function() {
          control.isHover = true;
          control.render();
        });
        bodyEl.addEventListener('mouseout', function() {
          control.isHover = false;
          control.render();
        });

        bodyEl.addEventListener('focus', function() {
          control.isFocused = true;
          control.render();
          //console.log('button focus');
        });

        bodyEl.addEventListener('blur', function() {
          control.isFocused = false;
          control.render();
          //console.log('button blur');
        });

        bodyEl.addEventListener('change', function(ev) {
          if (this.selectedIndex == -1) {
            control.reset();
          } else {
            var selOpt = this.options[this.selectedIndex];
            control.value = {
              value: selOpt.value,
              label: selOpt.text,
            };
          }

          // updateDataSourceParameter(control, options, instance);

          if (options.trigger) {
            if (!isFunction(options.trigger)) {
              console.error("combo's change trigger should be a function", options.trigger);
            } else {
              options.trigger.apply(this, [options, instance, control, path, ev]);
            }
          }

          if (options.event) {
            if (!isString(options.event)) {
              console.error("combo's change event should be a string", options.event);
            } else {
              instance.fire(
                options.event,
                pick(options, ['name', 'options', 'type', 'gridElement']),
                control,
                path,
                ev
              );
            }
          }

          control.doResetReload();
        });
      }
    }

    if (options.data && options.data.options !== undefined) {
      options.options = (options.data.options + '').split(',');
    }

    var stylePatch = {};
    var bodyStylePatch = {};
    var iconStylePatch = {};
    var iconEl = control.el.getElementsByClassName('icon')[0];
    bodyEl = control.el.getElementsByClassName('body')[0];
    var frameProps = [
      'top',
      'left',
      'right',
      'bottom',
      'margin',
      'position',
      'display',
      'width',
      'height',
    ];
    var defaultStyle = getDefaultControlStyle(control);

    merge(stylePatch, defaultStyle.normal);
    merge(bodyStylePatch, defaultStyle.body.normal);
    merge(iconStylePatch, defaultStyle.icon.normal);

    if (options.style && options.style.normal) {
      merge(stylePatch, pick(options.style.normal, frameProps));
      merge(bodyStylePatch, omit(options.style.normal, frameProps));
    }

    merge(stylePatch, control.frameStyle);

    if (options.icon) {
      merge(iconStylePatch, {
        width: options.icon.width,
        height: options.icon.height,
      });
    }

    if (control.isFocused) {
      merge(stylePatch, defaultStyle.focused);
      merge(bodyStylePatch, defaultStyle.body.focused);
      if (options.style && options.style.focused) {
        merge(stylePatch, pick(options.style.focused, frameProps));
        merge(bodyStylePatch, omit(options.style.focused, frameProps));
      }

      merge(iconStylePatch, defaultStyle.icon.focused);
    }

    if (control.isHover) {
      merge(stylePatch, defaultStyle.hover);
      merge(bodyStylePatch, defaultStyle.body.hover);
      if (options.style && options.style.hover) {
        merge(stylePatch, pick(options.style.hover, frameProps));
        merge(bodyStylePatch, omit(options.style.hover, frameProps));
      }

      merge(iconStylePatch, defaultStyle.icon.hover);
    }

    if (control.disabled) {
      merge(stylePatch, getDisabledStyle());
      if (options.style && options.style.disabled) {
        merge(stylePatch, options.style.disabled);
      }

      if (control.isLayoutMode) {
        delete stylePatch.pointerEvents;
      }
    }

    let scheme = lookAndFeel(options.lookAndFeel, stylePatch.height);
    // console.log('scheme', options.lookAndFeel, scheme);
    updateStyleByRef([stylePatch, bodyStylePatch, iconStylePatch], scheme);
    updateStyle(control.el, stylePatch, control.currentStyle);
    control.currentStyle = stylePatch;

    if (!control.isLayoutMode) {
      delete bodyStylePatch.padding;
    }

    updateStyle(bodyEl, bodyStylePatch, control.currentBodyStyle);
    if (control.isLayoutMode) {
      bodyEl.style.pointerEvents = 'none';
    }

    control.currentBodyStyle = bodyStylePatch;

    updateStyle(iconEl, iconStylePatch, control.currentIconStyle);
    control.currentIconStyle = iconStylePatch;

    // if (control.isLayoutMode) {
    //   return control.el;
    // }
    var selectedOption = control.value;
    if (isNumber(selectedOption)) {
      selectedOption += '';
    }

    if (selectedOption && isString(selectedOption)) {
      selectedOption = {
        label: selectedOption,
        value: selectedOption,
      };
    }

    if (selectedOption) {
      if (selectedOption.label === undefined) {
        selectedOption.label = selectedOption.value;
      }

      if (selectedOption.value === undefined) {
        selectedOption.value = selectedOption.label;
      }
    }

    let toSelect;
    let toSelectIndex = -1;

    const newOptions = options.options.map(function(d, i) {
      const option = {};
      if (isString(d)) {
        option.label = d;
        option.value = d;
      } else {
        option.label = d.label;
        option.value = d.value;
        if (d.selected) {
          option.selected = true;
          if (!toSelect) {
            toSelect = option;
          }
        }

        if (d.disabled) {
          option.disabled = true;
        }
      }

      if (option.label === undefined) {
        option.label = option.value;
      }

      if (option.value === undefined) {
        option.value = option.label;
      }

      if (
        selectedOption &&
        selectedOption.value == option.value &&
        (!toSelect ||
          toSelect.value !== selectedOption.value ||
          toSelect.label !== selectedOption.label)
      ) {
        toSelect = option;
      }

      return option;
    }).sort(function(a, b) {
      // Sort options alphabetically by label
      return a.label.localeCompare(b.label);
    });

    // Find the index of toSelect AFTER sorting
    if (toSelect) {
      toSelectIndex = newOptions.findIndex(function(opt) {
        return opt.value === toSelect.value && opt.label === toSelect.label;
      });
    }

    // If no option is selected and no defaultValue is set, select the first option
    if (!toSelect && !selectedOption && control.defaultValue === undefined && newOptions.length > 0) {
      toSelect = newOptions[0];
      toSelectIndex = 0;
      control.value = {
        value: toSelect.value,
        label: toSelect.label
      };
    }

    if (toSelect && !selectedOption && control.defaultValue === undefined) {
      options.value.defaultValue = toSelect;
    }

    if (!isEqual(control.renderedOptions, newOptions)) {
      control.renderedOptions = newOptions;
      var itemEls = document.createDocumentFragment();
      
      newOptions.forEach((option, index) => {
        const itemEl = document.createElement('option');
        itemEl.value = option.value;
        itemEl.text = option.label;
        if (option === toSelect) {
          itemEl.selected = true;
        }

        itemEl.style.backgroundColor = bodyStylePatch.color;
        itemEl.style.color = '#000'; //bodyStylePatch.backgroundColor;

        itemEls.appendChild(itemEl);
      });
      bodyEl.innerHTML = '';
      if (itemEls.childElementCount) {
        bodyEl.appendChild(itemEls);
      }

      if (!toSelect && selectedOption !== undefined) {
        bodyEl.selectedIndex = -1;
      }
    } else if (bodyEl.selectedIndex != toSelectIndex && selectedOption !== undefined) {
      bodyEl.selectedIndex = toSelectIndex;
    }

    return control.el;
  }
}
